/**
 * `useNotifyApiError()` — call-site shorthand for turning any caught API
 * error into the right user-facing UI.
 *
 *     const notify = useNotifyApiError()
 *     try { edenData(await eden.v1.something.post(payload)) }
 *     catch (err) { notify(err, { fallbackMessage: 'สร้างสินค้าไม่สำเร็จ' }) }
 *
 * Returns a function bound to the antd `<App>` context's `notification` +
 * `modal` instances (required in antd v6 — static methods silently no-op
 * once `<App>` is mounted at the root).
 *
 * The function:
 *   - Calls `mapApiError(err, opts)` to get a presentation
 *   - Dispatches `notification.error/warning/info` for toast presentations
 *   - Dispatches `Modal.error/warning/info` for modal presentations
 *   - Does nothing for silent presentations (caller handles inline)
 *   - Always returns the presentation so callers can inspect (e.g.
 *     `if (notify(err).kind === 'silent') setFormError(...)`).
 */
import { App } from "antd";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { type MapApiErrorOptions, mapApiError } from "@/lib/api-error";
import type { ErrorPresentation } from "@/lib/error-registry";
import { queryClient } from "@/lib/query-client";
import { queryKeys } from "@/lib/query-keys";
import { getApiError } from "@/types/api";

export type NotifyApiError = (err: unknown, opts?: MapApiErrorOptions) => ErrorPresentation;

export function useNotifyApiError(): NotifyApiError {
  const { notification, modal } = App.useApp();
  const { t, i18n } = useTranslation();

  return useCallback<NotifyApiError>(
    (err, opts) => {
      const p = mapApiError(err, opts);

      // Registry entries hold catalogue keys; a caller-supplied
      // `fallbackMessage` holds an already-translated literal. `exists` is what
      // tells the two apart without threading a flag through mapApiError.
      const tr = (value: string): string => (i18n.exists(value) ? t(value) : value);

      if (getApiError(err)?.code === "SUBSCRIPTION_EXPIRED") {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.billing.subscription,
        });
      }

      if (p.kind === "silent") return p;

      if (p.kind === "toast") {
        notification[p.severity]({
          message: tr(p.message),
          // Top-right corner, 6s auto-dismiss for errors; warnings/info also 6s.
          duration: 6,
        });
        return p;
      }

      // Modal kind — config calls (Modal.error / .warning / .info) need a
      // string key matching the severity. Action button mapped to `okText`
      // + onOk for path navigation; we use a hard nav because the modal
      // instance has no Router context.
      const action = p.action;
      const baseProps = {
        title: tr(p.title),
        content: tr(p.message),
        ...(action
          ? {
              okText: tr(action.label),
              onOk: () => {
                if (typeof window !== "undefined") window.location.assign(action.href);
              },
            }
          : {}),
      };
      if (p.severity === "error") modal.error(baseProps);
      else if (p.severity === "warning") modal.warning(baseProps);
      else modal.info(baseProps);

      return p;
    },
    [notification, modal, t, i18n],
  );
}
